import clsx from 'clsx';
import type * as React from 'react';

import { IS_MAC } from '@gitbook/slate-dev-environment';

/**
 * A util available for components to turn keyboard key names into their associated icon.
 */
export function prettifyShortcut(shortcut: string) {
    return shortcut
        .replace('backspace', '⌫')
        .replace('command', '⌘')
        .replace('cmd', '⌘')
        .replace('control', '⌃')
        .replace('ctrl', '⌃')
        .replace('option', '⌥')
        .replace('alt', '⌥')
        .replace('shift', '⇧')
        .replace('enter', '↩')
        .replace('meta', IS_MAC ? '⌘' : '^');
}

/**
 * Map a keyboard shortcut as pretty chars with each key in its own box
 */
function keyboardShortcutTextArray(shortcut: string) {
    const picked = shortcut
        .toLowerCase()
        // Split on a + or space character
        .split(/[+ ]/)
        .filter((char) => char !== '')
        .map((part) => prettifyShortcut(part).trim().toUpperCase());

    if (!picked) {
        return null;
    }

    return picked;
}

export type KeyboardShortcutProps = {
    /**
     * The keyboard shortcut string.
     */
    shortcut: string;

    /**
     * The size of the keyboard shortcut representation.
     * @default 'small'
     */
    size?: 'xsmall' | 'small' | 'medium';

    /**
     * Inverts style of the keyboard shortcut, used when the parent is dark e.g. with tooltips.
     */
    inverted?: boolean;

    /**
     * Optional styling for the keyboard shortcut.
     */
    className?: string;

    /**
     * Optional inline styling for the keyboard shortcut.
     * We discussed with design and it was decided to allow overriding to avoid many variants for single usecases.
     *
     * Overriding style should always be done after discussing with design.
     */
    style?: React.CSSProperties;
};

/**
 * Render a keyboard shortcut.
 */
export function KeyboardShortcut(props: KeyboardShortcutProps) {
    const { shortcut, size = 'small', inverted, className, style, ...rest } = props;

    return (
        <div className={clsx('flex items-center gap-1', className)}>
            {keyboardShortcutTextArray(shortcut)?.map((char, index) => (
                <span
                    key={index}
                    // Suppress hydration issues due to OS specific keyboard modifiers
                    suppressHydrationWarning
                    {...rest}
                    className={clsx(
                        'flex items-center justify-center rounded-md border border-[var(--sp-color-border-keyboard-shortcut-default)] border-b-2 bg-[var(--sp-color-bg-keyboard-shortcut-default)]',
                        char === '↩' ? 'font-mono' : null, // Using a monospace font to avoid an emoji being displayed for "Enter".
                        {
                            'border-[var(--sp-color-border-keyboard-shortcut-inverted)] border-b-2 bg-[var(--sp-color-bg-keyboard-shortcut-inverted)] text-[var(--sp-color-text-inverted-default)]':
                                inverted,
                            'max-h-4.5 min-w-4.5 px-0.5 text-[12px] text-ui-xsmall':
                                size === 'xsmall',
                            'max-h-5 min-w-5 px-1 text-ui-small': size === 'small',
                            'max-h-6 min-w-6 px-1 text-ui-action': size === 'medium',
                        }
                    )}
                    style={style}
                >
                    {char}
                </span>
            ))}
        </div>
    );
}
